// Admin › Agente de IA e R.A.G contra o WootDash (/ia — plano-vertex-ia.md): agentes de IA, base de
// conhecimento e o "Testar agente" (Vertex AI de Configurações Gerais). O teste não grava nada.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

export type AiAction = 'handoff' | 'set_variables' | 'goto_block' | 'end'
/** none = cada mensagem sozinha; conversation = lembra desta conversa; contact = também das anteriores do contato. */
export type AiMemoryMode = 'none' | 'conversation' | 'contact'

export interface AiAgent {
  name: string
  description: string | null
  instructions: string
  isActive: boolean
  model: string | null
  temperature: number | null
  allowedTools: string[]
  actions: AiAction[]
  knowledgeTags: string[]
  maxTurns: number
  maxToolCalls: number
  maxOutputTokens: number
  timeoutSeconds: number
  groundedOnly: boolean
  handoffWhenUnsure: boolean
  maskPersonalData: boolean
  memoryMode: AiMemoryMode
  memoryWindow: number
  contactMemoryDays: number
  /** Aviso de que é uma IA, mandado antes da primeira resposta (decisão do DPO). Obrigatório. */
  disclosureText: string
  /** Foto das mensagens dele no atendimento (https ou data:image). Vazio = avatar do bot. */
  avatarUrl?: string | null
  /** Copiloto do atendente: nas conversas destes Fluxos (número), a IA escreve a resposta no campo do atendente. */
  copilotEnabled?: boolean
  copilotFlowNumbers?: number[]
}

export interface AiAgentSummary extends AiAgent { id: string; updatedAt: string }

export interface KnowledgeDocument { title: string; content: string; source: string | null; tags: string[]; isActive: boolean }
/** indexStatus (busca por significado, fase 3): pending = na fila; indexed = trechos prontos; failed = indexError diz o porquê. */
export interface KnowledgeDocumentSummary extends KnowledgeDocument {
  id: string
  characters: number
  updatedAt: string
  indexStatus: 'pending' | 'indexed' | 'failed'
  indexedAt: string | null
  indexError: string | null
  chunkCount: number
  /** Parte de um PDF importado: as do mesmo arquivo têm o mesmo importId (nulo = documento digitado). */
  importId?: string | null
  importFileName?: string | null
  /** Referência do PDF original (abre por /anexos/{ref}). */
  importFileUrl?: string | null
  pageStart?: number | null
  pageEnd?: number | null
}

export interface AiAgentTestResult {
  success: boolean
  problem: string | null
  stage: string | null
  reply: string | null
  sentMessage: string | null
  sources: string[]
  model: string | null
  latencyMs: number | null
  inputTokens: number | null
  outputTokens: number | null
  memoryUsed: number
  note: string | null
  disclosure: string | null
  /** semantic = por significado; words = por palavra; mixed = os dois; none = nada achado. */
  searchMode: 'semantic' | 'words' | 'mixed' | 'none' | null
  /** Trechos usados. similarity 0–1 na busca por significado; null = achado por palavra. */
  matches: { title: string; similarity: number | null; excerpt: string }[] | null
  /** Passos da receita de ferramentas (simulador das dúvidas frequentes). */
  toolSteps?: { stepId: string; tool: string; args: Record<string, string>; ok: boolean; mode: 'real' | 'fake' | 'skipped'; output: string | null; problem: string | null }[] | null
}

/** Uma mensagem já trocada na conversa de teste. */
export interface AiTestTurn { papel: 'contato' | 'agente'; texto: string }

/** ok = salvou; senão problems diz o porquê (validação, não encontrado, rede). */
export type SaveResult<T> = { ok: true; item: T } | { ok: false; problems: string[] }

function headers(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

async function list<T>(token: string, path: string): Promise<{ items: T[]; problem: string | null }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}`, { headers: headers(token) })
    if (res.status === 401 || res.status === 403) return { items: [], problem: 'Sem permissão (precisa de ai.view).' }
    if (!res.ok) return { items: [], problem: `Erro ${res.status} ao carregar.` }
    return { items: await res.json(), problem: null }
  } catch {
    return { items: [], problem: 'Não foi possível falar com o servidor.' }
  }
}

async function save<T>(token: string, path: string, id: string | null, body: unknown): Promise<SaveResult<T>> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}${id ? `/${id}` : ''}`, {
      method: id ? 'PUT' : 'POST', headers: headers(token, true), body: JSON.stringify(body),
    })
    if (res.ok) return { ok: true, item: await res.json() }
    if (res.status === 401 || res.status === 403) return { ok: false, problems: ['Sem permissão (precisa de ai.edit).'] }
    if (res.status === 404) return { ok: false, problems: ['Não encontrado — alguém pode ter apagado. Recarregue a lista.'] }
    const data = await res.json().catch(() => null)
    return { ok: false, problems: data?.problems?.length ? data.problems : [`Erro ${res.status} ao salvar.`] }
  } catch {
    return { ok: false, problems: ['Não foi possível falar com o servidor.'] }
  }
}

async function remove(token: string, path: string): Promise<string | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}`, { method: 'DELETE', headers: headers(token) })
    if (res.ok || res.status === 404) return null
    return res.status === 401 || res.status === 403 ? 'Sem permissão (precisa de ai.edit).' : `Erro ${res.status} ao apagar.`
  } catch {
    return 'Não foi possível falar com o servidor.'
  }
}

export const listAiAgents = (token: string) => list<AiAgentSummary>(token, '/ia/agentes')
export const saveAiAgent = (token: string, id: string | null, agent: AiAgent) => save<AiAgentSummary>(token, '/ia/agentes', id, agent)
export const deleteAiAgent = (token: string, id: string) => remove(token, `/ia/agentes/${id}`)

export const listKnowledge = (token: string) => list<KnowledgeDocumentSummary>(token, '/ia/conhecimento')
export const saveKnowledge = (token: string, id: string | null, doc: KnowledgeDocument) => save<KnowledgeDocumentSummary>(token, '/ia/conhecimento', id, doc)
export const deleteKnowledge = (token: string, id: string) => remove(token, `/ia/conhecimento/${id}`)

/** Refaz os vetores de um documento (id) ou de todos (null). Devolve quantos entraram na fila, ou null se falhou. */
export async function reindexKnowledge(token: string, id: string | null): Promise<number | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/conhecimento${id ? `/${id}` : ''}/reindexar`, { method: 'POST', headers: wootdashAuthHeaders(token) })
    if (!res.ok) return null
    return (await res.json()).queued ?? 0
  } catch {
    return null
  }
}

export async function testAiAgent(token: string, agent: AiAgent, mensagem: string, historico: AiTestTurn[] = [], agenteId: string | null = null): Promise<AiAgentTestResult> {
  const failed = (problem: string): AiAgentTestResult => ({
    success: false, problem, stage: 'network', reply: null, sentMessage: null, sources: [], model: null, latencyMs: null, inputTokens: null, outputTokens: null, memoryUsed: 0, note: null, disclosure: null, searchMode: null, matches: null,
  })
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/agentes/testar`, { method: 'POST', headers: headers(token, true), body: JSON.stringify({ agent, mensagem, historico, agenteId }) })
    if (res.status === 401 || res.status === 403) return failed('Sem permissão (precisa de ai.edit).')
    const data = await res.json().catch(() => null)
    return data && typeof data.success === 'boolean' ? { ...data, sources: data.sources ?? [] } : failed(`Erro ${res.status} no servidor.`)
  } catch {
    return failed('Não foi possível falar com o servidor.')
  }
}

// ── Conversas (fase 2): cópias mascaradas das resolvidas, marcação e importação do histórico ──
export type AiConversationStatus = 'reference' | 'candidate' | 'excluded' | 'none'
export type AiConversationMark = 'reference' | 'exclude'

export interface AiConversationSummary {
  id: string
  origin: 'wootdash' | 'chatwoot'
  conversationId: string | null
  chatwootId: number | null
  flowNumber: number | null
  teamName: string | null
  resolvedAt: string
  rating: number | null
  messageCount: number
  status: AiConversationStatus
  manualMark: AiConversationMark | null
  markReason: string | null
  markedAt: string | null
  preview: string
}

export interface AiImportRun {
  id: string
  status: 'running' | 'done' | 'failed'
  since: string
  startedAt: string
  pages: number
  imported: number
  updated: number
  skipped: number
  failed: number
  lastError: string | null
  finishedAt: string | null
}

export interface AiConversationList {
  items: AiConversationSummary[]
  total: number
  counts: Partial<Record<AiConversationStatus, number>>
  lastImport: AiImportRun | null
}

export async function listAiConversations(
  token: string, filter: { situacao?: AiConversationStatus | null; fluxo?: number | null; origem?: string | null; pagina?: number },
): Promise<{ data: AiConversationList | null; problem: string | null }> {
  const q = new URLSearchParams()
  if (filter.situacao) q.set('situacao', filter.situacao)
  if (filter.fluxo) q.set('fluxo', String(filter.fluxo))
  if (filter.origem) q.set('origem', filter.origem)
  q.set('pagina', String(filter.pagina ?? 1))
  q.set('tamanho', '25')
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/conversas?${q}`, { headers: headers(token) })
    if (res.status === 401 || res.status === 403) return { data: null, problem: 'Sem permissão (precisa de ai.view).' }
    if (!res.ok) return { data: null, problem: `Erro ${res.status} ao carregar.` }
    return { data: await res.json(), problem: null }
  } catch {
    return { data: null, problem: 'Não foi possível falar com o servidor.' }
  }
}

export async function getAiConversation(token: string, id: string): Promise<{ summary: AiConversationSummary; transcript: string } | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/conversas/${id}`, { headers: headers(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

export async function markAiConversation(token: string, id: string, marcacao: AiConversationMark | null, motivo: string | null): Promise<SaveResult<AiConversationSummary>> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/conversas/${id}/marcacao`, {
      method: 'PUT', headers: headers(token, true), body: JSON.stringify({ marcacao, motivo }),
    })
    if (res.ok) return { ok: true, item: await res.json() }
    if (res.status === 401 || res.status === 403) return { ok: false, problems: ['Só o perfil Sistema marca conversas (permissão ai.mark).'] }
    const data = await res.json().catch(() => null)
    return { ok: false, problems: data?.problems?.length ? data.problems : [`Erro ${res.status} ao marcar.`] }
  } catch {
    return { ok: false, problems: ['Não foi possível falar com o servidor.'] }
  }
}

export async function startAiHistoryImport(token: string): Promise<{ run: AiImportRun | null; problem: string | null }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/conversas/importar`, { method: 'POST', headers: headers(token) })
    if (res.status === 401 || res.status === 403) return { run: null, problem: 'Sem permissão (precisa de ai.edit).' }
    const data = await res.json().catch(() => null)
    if (res.status === 409) return { run: data, problem: 'Já tem uma importação em andamento.' }
    return res.ok ? { run: data, problem: null } : { run: null, problem: `Erro ${res.status} ao iniciar.` }
  } catch {
    return { run: null, problem: 'Não foi possível falar com o servidor.' }
  }
}

/* ── Fase 4: conversas viram sugestões ─────────────────────────────── */

export type AiSuggestionStatus = 'suggested' | 'approved' | 'rejected'

export interface AiSuggestionSummary {
  id: string
  title: string
  content: string
  category: string | null
  flowNumber: number | null
  status: AiSuggestionStatus
  supportCount: number
  weightTotal: number
  periodFrom: string
  periodTo: string
  documentId: string | null
  reviewedAt: string | null
  rejectReason: string | null
  /** Documento ativo mais parecido (null = base sem documento indexado). */
  coveredBy: string | null
  coverage: number | null
  /** Nenhum documento chega perto: "falta escrever isto". */
  isGap: boolean
  suggestedTags: string[]
}

export interface AiExtractionRun {
  id: string
  trigger: 'night' | 'manual'
  startedAt: string
  queued: number
  extracted: number
  skipped: number
  failed: number
  limited: number
  done: number
}

export interface AiSuggestionSource {
  sourceConversationId: string
  question: string
  answer: string
  category: string | null
  weight: number
  rating: number | null
  origin: 'wootdash' | 'chatwoot'
  resolvedAt: string
}

export interface AiSuggestionList {
  items: AiSuggestionSummary[]
  counts: Record<AiSuggestionStatus, number>
  gaps: number
  latestRun: AiExtractionRun | null
}

export async function listAiSuggestions(token: string, situacao: AiSuggestionStatus | null, fluxo: number | null, lacunas: boolean): Promise<{ data: AiSuggestionList | null; problem: string | null }> {
  const params = new URLSearchParams()
  if (situacao) params.set('situacao', situacao)
  if (fluxo !== null) params.set('fluxo', String(fluxo))
  if (lacunas) params.set('lacunas', 'true')
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/sugestoes?${params}`, { headers: headers(token) })
    if (res.status === 403) return { data: null, problem: 'Sem permissão (precisa de ai.view).' }
    return res.ok ? { data: await res.json(), problem: null } : { data: null, problem: `Erro ${res.status} no servidor.` }
  } catch {
    return { data: null, problem: 'Não foi possível falar com o servidor.' }
  }
}

export async function getAiSuggestion(token: string, id: string): Promise<{ summary: AiSuggestionSummary; sources: AiSuggestionSource[] } | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/sugestoes/${id}`, { headers: headers(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

async function reviewCall(token: string, path: string, body?: unknown): Promise<SaveResult<AiSuggestionSummary>> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/sugestoes/${path}`, {
      method: 'POST',
      headers: { ...headers(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    })
    if (res.ok) return { ok: true, item: await res.json() }
    if (res.status === 404) return { ok: false, problems: ['Sugestão não encontrada.'] }
    if (res.status === 403) return { ok: false, problems: ['Sem permissão (precisa de ai.edit).'] }
    const data = await res.json().catch(() => null)
    return { ok: false, problems: data?.problems ?? [`Erro ${res.status} no servidor.`] }
  } catch {
    return { ok: false, problems: ['Não foi possível falar com o servidor.'] }
  }
}

export const approveAiSuggestion = (token: string, id: string, titulo: string, conteudo: string, etiquetas: string[]) =>
  reviewCall(token, `${id}/aprovar`, { titulo, conteudo, etiquetas })
export const rejectAiSuggestion = (token: string, id: string, motivo: string | null) => reviewCall(token, `${id}/rejeitar`, { motivo })
export const reopenAiSuggestion = (token: string, id: string) => reviewCall(token, `${id}/reabrir`)

export async function startAiExtraction(token: string): Promise<{ queued: number; run: AiExtractionRun | null } | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/sugestoes/extrair`, { method: 'POST', headers: headers(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

/* ── Fase 6: "O que o agente já sabe" ──────────────────────────────── */

export type AiVerdict = 'resolveria' | 'parcial' | 'transferiria' | 'erraria'
export type AiReadinessStatus = 'apto' | 'apto_revisao' | 'ainda_nao' | 'poucos_dados'

export interface AiReadinessItem { text: string; count: number }

export interface AiFlowReadiness {
  flowNumber: number | null
  status: AiReadinessStatus
  tested: number
  resolved: number
  partial: number
  transfer: number
  wrong: number
  serious: number
  resolvedRate: number
  resolvesWell: AiReadinessItem[]
  missing: AiReadinessItem[]
  documentsUsed: AiReadinessItem[]
  lastTestedAt: string | null
}

export interface AiEvaluationItem {
  sourceConversationId: string
  flowNumber: number | null
  question: string
  agentAnswer: string
  attendantAnswer: string
  verdict: AiVerdict
  serious: boolean
  reason: string | null
  topic: string | null
  sources: string[]
  testedAt: string
}

export interface AiEvaluationRun { id: string; flowNumber: number | null; startedAt: string; queued: number; evaluated: number; failed: number; limited: number; done: number }

export interface AiAgentReadiness { flows: AiFlowReadiness[]; evaluations: AiEvaluationItem[]; latestRun: AiEvaluationRun | null }

export async function getAiAgentReadiness(token: string, agentId: string, fluxo: number | null, veredito: AiVerdict | null): Promise<{ data: AiAgentReadiness | null; problem: string | null }> {
  const params = new URLSearchParams()
  if (fluxo !== null) params.set('fluxo', String(fluxo))
  if (veredito) params.set('veredito', veredito)
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/agentes/${agentId}/aptidao?${params}`, { headers: headers(token) })
    return res.ok ? { data: await res.json(), problem: null } : { data: null, problem: `Erro ${res.status} no servidor.` }
  } catch {
    return { data: null, problem: 'Não foi possível falar com o servidor.' }
  }
}

export async function startAiAgentEvaluation(token: string, agentId: string, fluxo: number | null): Promise<{ outcome: 'started' | 'nothingToTest' | 'agentNotFound'; run: AiEvaluationRun | null } | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/ia/agentes/${agentId}/testar-conversas`, {
      method: 'POST',
      headers: { ...headers(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ fluxo }),
    })
    if (!res.ok) return null
    const data = await res.json()
    const outcome = typeof data.outcome === 'string' ? data.outcome.charAt(0).toLowerCase() + data.outcome.slice(1) : ['started', 'nothingToTest', 'agentNotFound'][data.outcome]
    return { outcome, run: data.run }
  } catch {
    return null
  }
}
